// Configurações — equipe (operadores), permissões, unidades, especialidades.
// Operadores (/clinica/staff), unidades (/clinica/units) e especialidades
// (/clinica/specialties) na API real. Só permissões (matriz) segue em stub —
// não há modelo de permissões granular no backend (decisão pendente).
import AXIOS from "@/services/axios";

function adaptUnit(u = {}) {
  const a = u.address_detail;
  return {
    id: u.id,
    name: u.name,
    address: u.address,
    doctorCount: u.doctor_count,
    headquarter: !!u.headquarter,
    phone: u.phone_number || "",
    cnpj: u.cnpj || "",
    email: u.email || "",
    addressDetail: a
      ? {
          zipCode: a.zip_code || "",
          street: a.street || "",
          number: a.street_number || "",
          complement: a.additional_info || "",
          neighborhood: a.neighborhood || "",
          city: a.city || "",
          state: a.state || ""
        }
      : null
  };
}

function adaptClinicSpecialty(s = {}) {
  return {
    id: s.id,
    specialtyId: s.medical_specialty_id,
    name: s.name,
    defaultDuration: s.default_duration,
    suggested: !!s.suggested
  };
}

function unitBody(form) {
  const a = form.addressDetail;
  const body = {
    name: form.name,
    phone_number: form.phone,
    cnpj: form.cnpj,
    email: form.email,
    headquarter: form.headquarter
  };
  if (a && a.zipCode) {
    body.address_attributes = {
      zip_code: a.zipCode,
      street: a.street,
      street_number: a.number,
      additional_info: a.complement,
      neighborhood: a.neighborhood,
      city: a.city,
      state: a.state
    };
  }
  return body;
}

export class ClinicConfig {
  /** Equipe da clínica: memberships ativos + convites pendentes. */
  static operators() {
    return AXIOS.get("clinica/staff");
  }

  /** Convida um novo operador (secretária/admin) por e-mail. */
  /** `clinicId` fixa a clínica do convite (fora do módulo, ex.: painel do
   *  médico, onde a clínica ativa pode ser outra). */
  static inviteOperator(payload, { clinicId } = {}) {
    const body = {
      name: payload.name,
      email: payload.email,
      role: payload.role
    };
    // US-C12: sem e-mail, o convite vira só o link copiável.
    if (payload.sendEmail === false) body.send_email = false;
    return clinicId
      ? AXIOS.post("clinica/staff", body, {
          headers: { "X-Clinic-Id": clinicId }
        })
      : AXIOS.post("clinica/staff", body);
  }

  /** Muda o perfil de um operador ativo (admin | atendimento). */
  static updateOperator(id, role) {
    return AXIOS.patch(`clinica/staff/${id}`, { role });
  }

  /** Tira o acesso de um operador ativo. */
  static removeOperator(id) {
    return AXIOS.delete(`clinica/staff/${id}`);
  }

  static resendInvitation(id) {
    return AXIOS.post(`clinica/staff/invitations/${id}/resend`);
  }

  static cancelInvitation(id) {
    return AXIOS.delete(`clinica/staff/invitations/${id}`);
  }

  /** Matriz de permissões = reflexo do RBAC real (somente-leitura). */
  static permissions() {
    return AXIOS.get("clinica/permissions");
  }

  /** Nível do Atendimento numa área da clínica ativa (US-C11, só admin).
   *  Devolve a matriz atualizada. */
  static setPermission({ resource, level }) {
    return AXIOS.patch("clinica/permissions", { resource, level });
  }

  /** Cria o acesso na hora, com senha provisória devolvida uma vez (US-C12). */
  static createOperatorAccount({ name, email, phone, role }) {
    return AXIOS.post("clinica/staff/accounts", {
      name,
      email,
      phone_number: phone,
      role
    });
  }

  static units() {
    return AXIOS.get("clinica/units").then(({ data }) => ({
      data: (data || []).map(adaptUnit)
    }));
  }

  /** Cria (sem id) ou edita (com id) uma unidade da clínica (admin). */
  static saveUnit(form) {
    const body = unitBody(form);
    const request = form.id
      ? AXIOS.patch(`clinica/units/${form.id}`, body)
      : AXIOS.post("clinica/units", body);
    return request.then(({ data }) => ({ data: adaptUnit(data) }));
  }

  /** Desativa a unidade (a API recusa a sede e unidade com horário futuro). */
  static removeUnit(id) {
    return AXIOS.delete(`clinica/units/${id}`);
  }

  /** Endereço pelo CEP, via backend (`addresses/viacep`). */
  static lookupCep(cep) {
    const digits = String(cep || "").replace(/\D/g, "");
    return AXIOS.get(`addresses/viacep/${digits}`).then(({ data }) => ({
      data: (data && data.address) || {}
    }));
  }

  /** Catálogo global de especialidades (filtros e seletor). */
  static specialties() {
    return AXIOS.get("clinica/specialties");
  }

  /** Especialidades que a clínica atende, com a duração padrão. */
  static clinicSpecialties() {
    return AXIOS.get("clinica/clinic_specialties").then(({ data }) => ({
      data: (data || []).map(adaptClinicSpecialty)
    }));
  }

  static addClinicSpecialty({ specialtyId, defaultDuration }) {
    return AXIOS.post("clinica/clinic_specialties", {
      medical_specialty_id: specialtyId,
      default_duration: defaultDuration
    }).then(({ data }) => ({ data: adaptClinicSpecialty(data) }));
  }

  static removeClinicSpecialty(id) {
    return AXIOS.delete(`clinica/clinic_specialties/${id}`);
  }
}

export default ClinicConfig;
